import React, { useEffect, useState } from "react";
import DriverMap from "../components/DriverMap";
import { useGetLiveLocationsQuery } from "../store/api/adminApi";

export function activeLocations(locations, now) {
  return locations.filter(
    (fix) =>
      fix.connected === true &&
      ["customer", "driver"].includes(fix.role) &&
      Number.isFinite(fix.lat) &&
      Number.isFinite(fix.lng) &&
      Math.abs(fix.lat) <= 90 &&
      Math.abs(fix.lng) <= 180 &&
      Number.isFinite(fix.ts) &&
      now - fix.ts >= 0 &&
      now - fix.ts < 30000,
  );
}
export default function LiveUsers() {
  const { data, error, isLoading, refetch } = useGetLiveLocationsQuery(
    undefined,
    { pollingInterval: 2000, refetchOnFocus: true, refetchOnReconnect: true },
  );
  const [now, setNow] = useState(Date.now());
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  // Expire pins even when refresh fails, rather than presenting stale positions as live.
  const active = activeLocations(data?.data || [], now);
  const visible = active.filter((fix) =>
    `${fix.name || ""} ${fix.userId} ${fix.role}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const users = visible.map((fix) => ({
    id: fix.userId,
    name: fix.name || "App user",
    role: fix.role,
    lastLocation: fix,
  }));
  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <div className="flex flex-wrap justify-between gap-4 items-center mb-6">
        <div>
          <h1 className="text-2xl font-manrope-bold text-gray-900">
            Live users
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Active customers and drivers sharing their location from the app.
          </p>
        </div>
        <span className="bg-green-100 text-green-800 rounded-full px-4 py-2 text-sm">
          {active.length} active {active.length === 1 ? "user" : "users"}
        </span>
      </div>
      {!!error && (
        <div
          role="alert"
          className="bg-amber-50 text-amber-900 p-4 rounded-xl mb-4"
        >
          Could not refresh locations. Pins expire after 30 seconds.{" "}
          <button onClick={refetch} className="underline font-bold">
            Retry
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5">
        <div className="bg-white p-4 rounded-2xl border border-gray-100">
          <DriverMap
            drivers={users}
            selectedId={selectedId}
            onSelect={setSelectedId}
            label="Active users location map"
            entityLabel="User"
          />
          <p className="text-xs text-gray-500 mt-3">
            Updates every 2 seconds. Only connected users with a location
            received in the last 30 seconds appear. Location sharing requires
            app location permission and an internet connection.
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <label htmlFor="live-user-search" className="text-sm font-bold">
            Find an active user
          </label>
          <input
            id="live-user-search"
            placeholder="Search name or role…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full border rounded-xl p-3 mt-2 mb-4"
          />
          {isLoading && <p role="status">Loading locations…</p>}
          {!isLoading && !visible.length && (
            <p className="text-sm text-gray-500">
              {search
                ? "No active users match your search."
                : error
                  ? "No fresh locations available."
                  : "No users currently sharing a live location."}
            </p>
          )}
          <div className="space-y-2 max-h-[480px] overflow-y-auto">
            {visible.map((fix) => (
              <button
                key={fix.userId}
                onClick={() => setSelectedId(fix.userId)}
                aria-pressed={selectedId === fix.userId}
                className={`w-full text-left p-3 rounded-xl border ${selectedId === fix.userId ? "border-green-600 bg-green-50" : "border-gray-100 hover:bg-gray-50"}`}
              >
                <div className="flex justify-between gap-2">
                  <span className="font-bold text-sm">
                    {fix.name || "App user"}
                  </span>
                  <span className="text-xs text-green-700 capitalize">
                    {fix.role}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {fix.lat.toFixed(5)}, {fix.lng.toFixed(5)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Updated {Math.floor((now - fix.ts) / 1000)}s ago
                  {Number.isFinite(fix.accuracy)
                    ? ` · ±${Math.round(fix.accuracy)} m`
                    : ""}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
